import { useState } from "react";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Box, Divider, IconButton, Modal, Skeleton, Typography } from "@mui/material";

import { PaymentTransaction } from "../../../api/types/transactions.types";
import { useGetInvoiceDetails } from "../../../api/queries/useGetInvoiceDetails";
import { CloseButtonIcon } from "../../../assets/icons/transactions-modals-icons";
import InvoiceFigure from "../../../assets/images/invoice-modal.png";
import { CurrencyEnum } from "../../../../GoService/enum/service.enum";

const amountFormatter = (amount: number, currency: CurrencyEnum, locale: string = "en") => {
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount / 100);
};

export const InvoiceModal = ({
  invoice,
  open,
  closeModal,
}: {
  invoice?: PaymentTransaction;
  open: boolean;
  closeModal: () => void;
}) => {
  const { t, i18n } = useTranslation("plans", { keyPrefix: "component.invoiceModal" });
  const { t: tEnum } = useTranslation("plans", { keyPrefix: "enum" });

  const { data: invoiceDetails, isLoading } = useGetInvoiceDetails(invoice?.id);

  const dateLocale = i18n.language === "ar" ? ar : enUS;
  const formattedAmount = invoiceDetails
    ? amountFormatter(invoiceDetails.amountDue, invoiceDetails.currency, i18n.language)
    : "";

  if (isLoading) {
    return (
      <Modal open={open} onClose={closeModal}>
        <Box sx={styles.modalContainer}>
          <Box sx={styles.modalContent}>
            <Box sx={styles.modalHeader}>
              <IconButton size="small" onClick={closeModal}>
                <CloseButtonIcon />
              </IconButton>
            </Box>
            <Divider flexItem orientation="horizontal" />
            <Box sx={styles.modalContent}>
              <Skeleton variant="rectangular" width={157} height={120} sx={{ borderRadius: "12px" }} />
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="text" width={150} height={24} />
            </Box>
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={closeModal}>
      <Box sx={styles.modalContainer}>
        <Box sx={styles.modalContent}>
          <Box sx={styles.modalHeader}>
            <IconButton size="small" onClick={closeModal}>
              <CloseButtonIcon />
            </IconButton>
          </Box>
          <Divider flexItem orientation="horizontal" />
          <Box sx={styles.modalBody}>
            <Box sx={styles.invoiceContainer}>
              <Box component="img" src={InvoiceFigure} alt="Transaction" sx={styles.invoiceFigure} />
              <Typography sx={styles.invoiceAmount}>{formattedAmount || "--"}</Typography>
              <Typography sx={styles.invoiceStatus}>{t("invoicePaid")}</Typography>
            </Box>
            <Box sx={{ ...styles.infoCard, ...styles.infoCardColumns }}>
              <Box sx={styles.infoCardRow}>
                <Typography sx={styles.infoLabel}>{t("invoiceNumber")}</Typography>
                <Typography sx={styles.infoValue}>{invoiceDetails?.invoiceNumber || "--"}</Typography>
              </Box>
              <Box sx={styles.infoCardRow}>
                <Typography sx={styles.infoLabel}>{t("date")}</Typography>
                <Typography sx={styles.infoValue}>
                  {invoiceDetails?.invoiceDate
                    ? format(new Date(invoiceDetails?.invoiceDate), "MMM dd, yyyy hh:mm a", { locale: dateLocale })
                    : "--"}
                </Typography>
              </Box>
              <Box sx={styles.infoCardRow}>
                <Typography sx={styles.infoLabel}>{t("card")}</Typography>
                <Typography sx={styles.infoValue}>{invoiceDetails?.cardNumber || "--"}</Typography>
              </Box>
              <Box sx={styles.infoCardRow}>
                <Typography sx={styles.infoLabel}>{t("reasonOfPayment")}</Typography>
                <Typography sx={styles.infoValue}>
                  {invoiceDetails?.reasonOfPayment
                    ? tEnum(`transaction_payment_destination.${invoiceDetails.reasonOfPayment}`)
                    : "--"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export const useInvoiceModal = () => {
  const [open, setOpen] = useState(false);
  const [invoice, setInvoice] = useState<PaymentTransaction | undefined>(undefined);

  const handleOpenModal = (invoice: PaymentTransaction) => {
    setOpen(true);
    setInvoice(invoice);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setInvoice(undefined);
  };
  return { open, invoice, handleOpenModal, handleCloseModal };
};

const styles = {
  modalContainer: {
    width: "512px",
    height: "fit-content",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "16px",
    background: "#FFF",
    boxShadow: "0px 4px 50px 0px rgba(0, 0, 0, 0.08), 0px 4px 6px 0px rgba(0, 0, 0, 0.05)",
  },
  modalHeader: {
    width: "100%",
    display: "flex",
    padding: "24px 32px 0",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  modalContent: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  modalBody: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
    padding: "0 32px 32px",
  },
  invoiceContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  },
  invoiceFigure: {
    width: "157px",
    height: "120px",
  },
  invoiceAmount: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "32px",
  },
  invoiceStatus: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  infoCard: {
    width: "100%",
    borderRadius: "12px",
    background: "var(--Gray---25, #F7F8F8)",
    padding: "16px",
  },
  infoLabel: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  infoValue: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  infoCardColumns: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  infoCardRow: {
    display: "flex",
    justifyContent: "space-between",
  },
};
